/**
 * Route manifest — the single structural list of every route the app mounts.
 *
 * Pure data (no JSX) so it can be compared to `data/pageRegistry.js` in Jest and
 * at runtime (`lib/routeAudit.js`). `routes.jsx` maps each `id` to its element;
 * `App.js` renders the tree from here. Add a route = add it here + its element.
 *
 * kind: "page" (renders a screen) · "layout" (parent shell, not a page) ·
 *       "redirect" (Navigate) · "wildcard" (catch-all)
 */
export const routeManifest = [
  { id: "home", path: "/" },
  { id: "about", path: "/about" },
  { id: "contact", path: "/contact" },
  { id: "why", path: "/why" },
  { id: "privacy", path: "/privacy" },
  { id: "terms", path: "/terms" },
  { id: "strategy", path: "/strategy" },
  { id: "habit-loop-strategy", path: "/habit-loop-strategy" },
  { id: "unsubscribed", path: "/unsubscribed" },
  { id: "money-pillars", path: "/money-pillars" },
  { id: "income-strategies", path: "/income-strategies" },
  { id: "habit-stacking", path: "/habit-stacking" },
  { id: "wealth-formula", path: "/wealth-formula" },
  { id: "spending-plan-vs-budget", path: "/spending-plan-vs-budget" },
  { id: "compare", path: "/vs/:slug" },
  { id: "ynab-alternative", path: "/ynab-alternative" },
  { id: "balance-calculator", path: "/balance-calculator" },
  { id: "press", path: "/press" },
  { id: "couples", path: "/couples" },
  { id: "quiz", path: "/quiz" },
  { id: "quiz-path-fix", path: "/quiz/*", kind: "redirect" },
  { id: "contest-rules", path: "/contest-rules" },
  { id: "preview-half-loop", path: "/preview/half-loop" },
  { id: "links", path: "/links" },
  { id: "preview-ugc", path: "/preview/ugc" },
  { id: "login", path: "/login" },
  { id: "register", path: "/register" },
  { id: "forgot-password", path: "/forgot-password" },
  { id: "reset-password", path: "/reset-password" },
  { id: "verify-email", path: "/verify-email" },
  { id: "new-device", path: "/security/new-device" },
  {
    id: "app",
    path: "/app",
    kind: "layout",
    children: [
      { id: "dashboard", index: true },
      { id: "calendar", path: "calendar" },
      { id: "bills", path: "bills" },
      { id: "scan", path: "scan" },
      { id: "habits", path: "habits" },
      { id: "wealth-plan", path: "wealth-plan" },
      { id: "net-worth", path: "net-worth" },
      { id: "whats-new", path: "whats-new" },
      { id: "pricing", path: "pricing" },
      { id: "settings", path: "settings" },
      { id: "member-contact", path: "contact" },
      { id: "member-quiz", path: "quiz" },
      { id: "member-strategy", path: "strategy" },
      { id: "member-couples", path: "couples" },
      { id: "admin-inbox", path: "admin/inbox" },
      { id: "admin-chat", path: "admin/chat" },
      { id: "admin-operations-redirect", path: "admin/operations", kind: "redirect" },
      { id: "admin-metrics", path: "admin/metrics" },
      { id: "admin-cmo", path: "admin/cmo" },
      { id: "admin-cmo-metrics-redirect", path: "admin/cmo/metrics", kind: "redirect" },
      { id: "admin-cmo-creative", path: "admin/cmo/creative" },
      { id: "admin-cmo-videos", path: "admin/cmo/videos" },
      { id: "admin-cmo-library", path: "admin/cmo/library" },
      { id: "admin-cmo-operations", path: "admin/cmo/operations" },
      { id: "admin-broadcast", path: "admin/broadcast" },
      { id: "admin-users", path: "admin/users" },
    ],
  },
  { id: "catch-all", path: "*", kind: "wildcard" },
];

function joinPath(parent, child) {
  if (!parent) return child;
  if (child === undefined) return parent;
  return `${parent.replace(/\/$/, "")}/${child.replace(/^\//, "")}`;
}

/** Flat `[{ id, path (absolute), kind }]` — layouts included with kind "layout". */
export function flattenManifest(nodes = routeManifest, parentPath = "", out = []) {
  for (const n of nodes) {
    const path = n.index ? parentPath : joinPath(parentPath, n.path);
    out.push({ id: n.id, path, kind: n.kind || "page" });
    if (n.children) flattenManifest(n.children, path, out);
  }
  return out;
}
